import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { fetchDuplicateHealth } from "@/lib/api";

// Warns admins when the database has duplicate slugs. Duplicates can break the
// unique index and, on a restart, take the whole app down — so we surface them
// early with the exact collections/slugs to clean. Dismissible per session.
export default function AdminHealthBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("dh_dupe_banner_dismissed") === "1"
  );

  useEffect(() => {
    fetchDuplicateHealth().then(setData).catch(() => setData(null));
  }, []);

  if (dismissed || !data || !data.extra_count) return null;

  const dismiss = () => {
    sessionStorage.setItem("dh_dupe_banner_dismissed", "1");
    setDismissed(true);
  };

  const lines = Object.entries(data.duplicates || {}).map(([coll, items]) => ({
    coll,
    text: items.map((i) => `${i.slug} (×${i.count})`).join(", "),
  }));

  return (
    <div data-testid="admin-health-banner" className="mb-6 border-l-4 border-amber-500 bg-amber-50 px-4 py-3.5">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">
            {data.extra_count} duplicate {data.extra_count === 1 ? "entry" : "entries"} found in your database
          </p>
          <p className="mt-1 text-xs leading-relaxed text-amber-800">
            Duplicate slugs can break the unique index and cause the site to fail on restart
            (returning “signed out” / server errors). Please remove or re-slug the duplicates below,
            then everything stays healthy.
          </p>
          <ul className="mt-2 space-y-1">
            {lines.map((l) => (
              <li key={l.coll} data-testid={`dupe-${l.coll}`} className="text-xs text-amber-900">
                <span className="font-medium uppercase tracking-wide">{l.coll}:</span> {l.text}
              </li>
            ))}
          </ul>
        </div>
        <button data-testid="admin-health-dismiss" onClick={dismiss} aria-label="Dismiss"
          className="shrink-0 text-amber-700 hover:text-amber-900">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
